import { makeObservable, observable, action } from 'mobx';
import { SectionDto } from '../../types';
import { InputBaseModel, NodeBaseModel } from '..';
import { InputFactory } from '../../factories';
import { UiUtils } from '../../misc';
import { SourceModelBase } from '../../../custom_shared/models';
import { InstanceUtils } from '../../misc';

export default class SectionModel {
    readonly id: string;

    readonly name: string;

    inputs: InputBaseModel[] = [];

    description: string | null;

    isDisabled: boolean;

    isHidden: boolean;

    expanded: boolean = false;

    constructor(
        private readonly node: NodeBaseModel,
        data: SectionDto,
        sources: SourceModelBase[]
    ) {
        this.id = data.id;
        this.name = data.name;
        this.description = data.description;
        this.isDisabled = data.isDisabled;
        this.isHidden = data.isHidden;

        const inputs = InputFactory.createInputs(this.node, data.inputs, sources);

        if (InstanceUtils.hasSectionSyncManager(this.node)) {
            this.node.sectionSyncManager.markReadonlyInputs(this.id, inputs);
        }

        this.inputs = inputs;

        makeObservable(this, {
            inputs: observable,
            description: observable,
            isDisabled: observable,
            isHidden: observable,
            expanded: observable,
            setExpanded: action.bound,
            toggleExpanded: action.bound,
            update: action
        });

        this.refreshExpandedState();
    }

    refreshExpandedState() {
        this.setExpanded(UiUtils.shouldExpandCollapse(this.inputs));
    }

    setExpanded(expanded: boolean) {
        this.expanded = expanded;
    }

    toggleExpanded() {
        this.setExpanded(!this.expanded);
    }

    getInputById(id: string) {
        return this.inputs.find(input => input.id === id);
    }

    update(data: SectionDto, sources: SourceModelBase[]) {
        this.description = data.description;
        this.isDisabled = data.isDisabled;
        this.isHidden = data.isHidden;
        this.inputs = InputFactory.createOrUpdateInputs(this.node, this.inputs, data.inputs, sources);
    }

    get dto(): SectionDto {
        return {
            id: this.id,
            name: this.name,
            inputs: this.inputs.map(input => input.dto),
            isDisabled: this.isDisabled,
            isHidden: this.isHidden,
            description: this.description
        };
    }
}
